import { useEffect, useState } from 'react'
import AppShell from '@/components/AppShell'
import ImageLightbox from '@/components/ImageLightbox'
import LoginPage from '@/components/LoginPage'
import { appFetch } from '@/data/api/app-fetch'
import { localDb } from '@/data/local/database'
import { restoreCloudSession, logoutCloud } from '@/services/auth/cloud-session'
import { getActiveIdentity } from '@/services/identity/local-identity'
import { startLifecycleServices } from '@/services/lifecycle'
import { repairCloudSnapshotOnce, repairCriticalUserStatsOnce, requestSync } from '@/services/sync/sync-engine'
import { Capacitor } from '@capacitor/core'
import { FocusSoundscapeProvider } from '@/features/soundscape/FocusSoundscapeProvider'
import { migrateRemoveMistakeTag } from '@/services/migrations/remove-mistake-tag'

declare const __APP_WEB_VERSION__: string

interface UserProfile {
  id: string
  displayName: string
  statusSignature?: string
  examName?: string
  examDate?: string
  mockName?: string
  mockDate?: string
  examType?: string
  studyModules?: string[]
  focusTheme?: string
}

async function loadLocalProfile(): Promise<UserProfile> {
  const response = await appFetch('/api/auth/me')
  const data = await response.json() as { user: UserProfile }
  return data.user
}

function LoadingScreen() {
  return (
    <div className="app-loading">
      <div className="app-loading-inner">
        <span className="app-loading-logo"><img src="/app-icon-192.png" alt="闪念上岸" /></span>
        <div className="app-loading-text">闪念上岸 · 正在连接学习空间</div>
      </div>
    </div>
  )
}

export default function App() {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const isAuthenticated = user !== null
  const view = new URLSearchParams(window.location.search).get('view') || 'home'

  useEffect(() => {
    let active = true
    void (async () => {
      try {
        const identity = await getActiveIdentity()
        try {
          await restoreCloudSession()
          await repairCriticalUserStatsOnce()
          await requestSync()
          if (active) setUser(await loadLocalProfile())
          // Notes may contain large base64 images. Restore that library in the
          // background after the real study/practice totals are already visible.
          void repairCloudSnapshotOnce().then(() => requestSync()).catch(() => undefined)
        } catch {
          if (!navigator.onLine && identity.authenticated) {
            const localUser = await localDb.localUsers.get(identity.userId)
            if (active) setUser({
              id: identity.userId,
              displayName: localUser?.displayName || '离线用户',
            })
          } else if (active) {
            setUser(null)
          }
        }
      } catch {
        // IndexedDB 或其他初始化失败 → 直接显示登录页
        if (active) setUser(null)
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    const refreshProfile = () => {
      void loadLocalProfile().then(setUser).catch(() => undefined)
    }
    const profileUpdated = (event: Event) => {
      const detail = (event as CustomEvent<UserProfile>).detail
      if (detail) setUser(current => current ? { ...current, ...detail } : detail)
      else refreshProfile()
    }
    window.addEventListener('study-tool:synced', refreshProfile)
    window.addEventListener('study-tool:profile-updated', profileUpdated)
    return () => {
      window.removeEventListener('study-tool:synced', refreshProfile)
      window.removeEventListener('study-tool:profile-updated', profileUpdated)
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) return
    let stop = () => {}
    let disposed = false
    void startLifecycleServices().then(cleanup => {
      if (disposed) cleanup()
      else stop = cleanup
    })
    // v2.7.5：清理其他模块冗余的「错题记录」标签及其画布标签节点（幂等并同步）
    void migrateRemoveMistakeTag()
    return () => {
      disposed = true
      stop()
    }
  }, [isAuthenticated])

  // Session expired (401) → force back to the login page
  useEffect(() => {
    const onSessionExpired = () => {
      void logoutCloud().finally(() => {
        setUser(null)
      })
    }
    window.addEventListener('study-tool:session-expired', onSessionExpired)
    return () => window.removeEventListener('study-tool:session-expired', onSessionExpired)
  }, [])

  // Auto-update check for Android
  useEffect(() => {
    if (!isAuthenticated || !Capacitor.isNativePlatform()) return
    void (async () => {
      try {
        const resp = await fetch('https://shangancard.me/app-updates/stable/manifest.json', { cache: 'no-store' })
        if (!resp.ok) return
        const manifest = await resp.json()
        const payload = JSON.parse(manifest.payload)
        const current = typeof __APP_WEB_VERSION__ !== 'undefined' ? __APP_WEB_VERSION__ : '1.0.0'
        if (payload.version !== current && confirm(`发现新版本 v${payload.version}，是否立即更新？`)) {
          window.open('https://shangancard.me/download/ShanganCard.apk', '_blank')
        }
      } catch { /* silent */ }
    })()
  }, [isAuthenticated])

  if (loading) return <LoadingScreen />
  if (!user) {
    return <LoginPage onLogin={async () => {
      await repairCriticalUserStatsOnce()
      await requestSync()
      setUser(await loadLocalProfile())
      void repairCloudSnapshotOnce().then(() => requestSync()).catch(() => undefined)
    }} />
  }
  return (
    <>
      <FocusSoundscapeProvider>
        <AppShell user={user} view={view} />
      </FocusSoundscapeProvider>
      <ImageLightbox />
    </>
  )
}
